import type { RecordItem, RecordRevision } from '@/entities/Record';
import { extractApiErrorCode } from '@/shared/lib';
import type { RecordLinkContainer } from '../api/matrices.api';
import {
  ADD_RECORD_ERROR_MESSAGE,
  MATRICES_NOT_ELIGIBLE_FOR_LINK_MESSAGE,
  MATRIX_ALREADY_LINKED_MESSAGE,
  MATRIX_PREFIX_PINNED_TO_OTHER_MODULE_MESSAGE,
} from '../config/messages';
import type { DirectoryTreeNode } from './types';
import { extractNumericId } from './directoryTree.utils';

const MATRIX_LINK_ERROR_MESSAGES: ReadonlyMap<string, string> = new Map([
  ['MATRIX_ALREADY_LINKED', MATRIX_ALREADY_LINKED_MESSAGE],
  ['MATRIX_PREFIX_PINNED_TO_OTHER_MODULE', MATRIX_PREFIX_PINNED_TO_OTHER_MODULE_MESSAGE],
  ['MATRICES_NOT_FOUND_FOR_LINK', MATRICES_NOT_ELIGIBLE_FOR_LINK_MESSAGE],
  ['MATRICES_IN_DRAFT', MATRICES_NOT_ELIGIBLE_FOR_LINK_MESSAGE],
  ['MATRICES_STATUS_NOT_LINKABLE', MATRICES_NOT_ELIGIBLE_FOR_LINK_MESSAGE],
]);

interface LinkedMatrixSelection {
  record: Pick<RecordItem, 'code'>;
  revision: Pick<RecordRevision, 'id' | 'revision'>;
}

export interface MatrixLinkErrorToast {
  title: string;
  message?: string;
}

function extractApiMatrixIds(error: unknown): number[] {
  if (typeof error !== 'object' || error === null) {
    return [];
  }

  const data = (error as { response?: { data?: unknown } }).response?.data;

  if (typeof data !== 'object' || data === null) {
    return [];
  }

  const { matrixIds } = data as { matrixIds?: unknown };

  if (!Array.isArray(matrixIds)) {
    return [];
  }

  return matrixIds.filter((id): id is number => typeof id === 'number' && Number.isInteger(id));
}

function formatSelectionLabel({ record, revision }: LinkedMatrixSelection): string {
  return `${record.code} (REV ${String(revision.revision).padStart(2, '0')})`;
}

/**
 * The affected line is built from the user's own selections, so the toast names records the way
 * the modal showed them and never exposes matrix ids.
 */
export function resolveMatrixLinkErrorToast(
  error: unknown,
  selections: readonly LinkedMatrixSelection[]
): MatrixLinkErrorToast {
  const errorCode = extractApiErrorCode(error);
  const title = errorCode === undefined ? undefined : MATRIX_LINK_ERROR_MESSAGES.get(errorCode);

  if (title === undefined) {
    return { title: ADD_RECORD_ERROR_MESSAGE };
  }

  const affectedIds = new Set(extractApiMatrixIds(error));
  const affectedLabels = selections
    .filter(({ revision }) => affectedIds.has(revision.id))
    .map(formatSelectionLabel);

  return affectedLabels.length > 0 ? { title, message: affectedLabels.join(', ') } : { title };
}

export function resolveRecordLinkContainer(
  node: DirectoryTreeNode | null | undefined
): RecordLinkContainer | null {
  if (!node) {
    return null;
  }

  switch (node.kind) {
    case 'module': {
      const id = extractNumericId(node.id, 'module-');
      return id === undefined ? null : { type: 'module', id };
    }
    case 'folder': {
      const id = extractNumericId(node.id, 'folder-');
      return id === undefined ? null : { type: 'folder', id };
    }
    default:
      return null;
  }
}

export function buildEligibleMatricesSearchUrl(
  node: DirectoryTreeNode | null | undefined
): string | null {
  const container = resolveRecordLinkContainer(node);

  if (!container) {
    return null;
  }

  return container.type === 'module'
    ? `/modules/${container.id}/eligible-matrices`
    : `/folders/${container.id}/eligible-matrices`;
}
